import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Download, ArrowLeft, Share2, Sparkles, Image as ImageIcon, Loader2 } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { API_BASE } from "../lib/api";

export default function Result() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [blobSrc, setBlobSrc] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(null);

  const url = sp.get("url");
  const full = useMemo(() => {
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : `${API_BASE}${url}`;
  }, [url]);

  // Fetch image via JS so we can include the ngrok header
  useEffect(() => {
    if (!full) { setImgLoading(false); return; }
    let cancelled = false;
    setImgLoading(true);
    setImgError(null);

    fetch(full, {
      cache: "no-store",
      headers: { "ngrok-skip-browser-warning": "true" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        setBlobSrc(window.URL.createObjectURL(blob));
        setImgLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Image load error:", err);
        setImgError(err.message);
        setImgLoading(false);
      });

    return () => { cancelled = true; };
  }, [full]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => { if (blobSrc) window.URL.revokeObjectURL(blobSrc); };
  }, [blobSrc]);

  const handleDownload = async () => {
    if (!full) return;
    try {
      setDownloading(true);
      const response = await fetch(full, { cache: "no-store", headers: { "ngrok-skip-browser-warning": "true" } });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `furnished-room-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed", error);
      alert("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!full) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Interior Design Result", text: "Check out this AI-generated interior design!", url: full });
      } else {
        await navigator.clipboard.writeText(full);
        alert("Image link copied to clipboard.");
      }
    } catch (e) { console.error("Share failed", e); }
  };

  const cardStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-sm)",
    padding: "1.25rem",
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-soft)" }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6 animate-fade-up">

        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => nav("/interior-design-ai/dashboard")}
            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 transition-all"
            style={{ color: "var(--text-secondary)", border: "1px solid var(--border-strong)", background: "var(--surface)" }}
          >
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleShare}
              className="p-2 rounded-lg transition-all"
              style={{ color: "var(--text-secondary)", border: "1px solid var(--border)", background: "var(--surface)" }}
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading || !full}
              className="btn-primary-glow inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, var(--orange), var(--orange-light))" }}
            >
              <Download className="w-4 h-4" />
              {downloading ? "Downloading..." : "Download Render"}
            </button>
          </div>
        </div>

        {/* Title */}
        <div>
          <h2
            className="text-2xl font-bold flex items-center gap-2"
            style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}
          >
            Design Generated
            <Sparkles className="w-5 h-5" style={{ color: "var(--orange)" }} />
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Your space has been successfully transformed by AI
          </p>
        </div>

        {/* Result image */}
        <div style={{ ...cardStyle, padding: 8 }}>
          <div
            className="relative aspect-video overflow-hidden"
            style={{ borderRadius: "var(--radius-md)", background: "var(--surface-muted)" }}
          >
            {imgLoading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ color: "var(--text-muted)" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--orange)" }} />
                <p className="text-sm font-medium">Loading render...</p>
              </div>
            ) : blobSrc ? (
              <img src={blobSrc} className="w-full h-full object-cover" alt="Generated Result" style={{ borderRadius: "var(--radius-md)" }} />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ color: "var(--text-muted)" }}>
                <ImageIcon className="w-12 h-12" />
                <p className="text-sm font-medium">{imgError || "No result found"}</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div style={cardStyle}>
            <h4 className="font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
              Design Summary
            </h4>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Our AI analyzed your room's spatial constraints and lighting to deliver this composition.
              The style matches your requested parameters while maintaining optimal flow.
            </p>
          </div>
          <div style={cardStyle}>
            <h4 className="font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
              Next Steps
            </h4>
            <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--text-secondary)" }}>
              Download this high-resolution render or try another style by going back to the dashboard.
            </p>
            <Link
              to="/interior-design-ai/dashboard"
              className="text-sm font-semibold transition-colors"
              style={{ color: "var(--orange)" }}
            >
              Try another pipeline →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
